import React, {useEffect, useState, useCallback} from 'react';
import {Portal} from '../../common/Portal.jsx';
import {Adalicon} from './../../common/Icons.jsx';
import Button from '../../bootstrap/Button.jsx';
import {fetchJson} from '../../../helpers/fetch.js';
import ToggleSwitch from '../../common/ToggleSwitch.jsx';
import {closeModal, Modal as ModalComponent} from '../../common/Modal.jsx';
import update from 'immutability-helper';
import {DndProvider} from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import {DragHandle, DropArea} from '../../common/DragNDrop.jsx';

import { datagrid_modify_presets_in_batch_path } from '~/routes'

const MANAGE_PRESETS_MODAL_ID = 'manage-presets-modal';
const DND_ITEM_TYPE = 'row';

const ManagePresetsModalTrigger = ({permissions, presets, setPresets, restoreState, resetState, context_name, datagrid_id}) => {
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [displayedPresets, setDisplayedPresets] = useState(presets);
  const [editRowId, setEditRowId] = useState(null);
  const [modifiedPresets, setModifiedPresets] = useState([]);

  const handleCloseModal = () => {
    if (modifiedPresets.length != 0 && !confirm(I18n.t('cancel_confirm'))) return;
    closeModal(MANAGE_PRESETS_MODAL_ID);
    setOpenModal(false)
  };
  const handleOpenModal = () => setOpenModal(true);

  const handleDeletePreset = (preset) => {
    if (!confirm(I18n.t('confirm_delete_preset'))) return;
    setDisplayedPresets(oldPresets => oldPresets.filter(oldPreset => oldPreset.id != preset.id));
    registerModification(preset.id, 'deleted', true);
  }

  const handleChangeDefault = (preset, attr) => {
    if (preset[attr] == true) {
      setDisplayedPresets(oldPresets =>
        oldPresets.map((oldPreset) => ({...oldPreset, [attr]: false}))
      )
    } else {
      setDisplayedPresets(oldPresets =>
        oldPresets.map(oldPreset => ({...oldPreset, [attr]: (oldPreset.id == preset.id)}))
      );
    }
  }

  const updatePresetProperty = (idToUpdate, key, newProperty) => {
    registerModification(idToUpdate, key, newProperty)

    setDisplayedPresets(oldPresets => {
      const indexToUpdate = oldPresets.findIndex(preset => preset.id == idToUpdate);
      const updatedPreset = {...oldPresets[indexToUpdate], [key]: newProperty};
      return [
        ...oldPresets.slice(0, indexToUpdate),
        updatedPreset,
        ...oldPresets.slice(indexToUpdate + 1)
      ];
    })
  }

  const registerModification = (modifiedPresetId, modifiedPropertyKey, newPropertyValue) => {
    for (const preset of presets) {
      if (preset.id != modifiedPresetId) continue;
      const nothingActuallyModified = preset[modifiedPropertyKey] == newPropertyValue;
      if (nothingActuallyModified) return;
    }

    const newModification = {
      presetId: modifiedPresetId,
      key: modifiedPropertyKey,
      value: newPropertyValue
    }
    setModifiedPresets(oldRegister => {
      for (const modificationIndex in oldRegister) {
        const thisModificationShouldBeReplacedWithNew = (
            (oldRegister[modificationIndex].presetId == modifiedPresetId) &&
            (modifiedPropertyKey == 'deleted' || modifiedPropertyKey == oldRegister[modificationIndex].key)
        )
        if (thisModificationShouldBeReplacedWithNew) {
          return [
            ...oldRegister.slice(0, modificationIndex),
            newModification,
            ...oldRegister.slice(modificationIndex + 1)
          ];
        }
      }
      const brandNewModification = [...oldRegister, newModification];
      return brandNewModification;
    })
  }

  const moveRow = useCallback((dragIndex, hoverIndex) => {
    const dragRow = displayedPresets[dragIndex];
    setDisplayedPresets((prevRows) =>
      update(prevRows, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRow],
        ]
      })
    );
  }, [displayedPresets]);

  useEffect(() => {
    setDisplayedPresets(presets);
    setEditRowId(null);
    setModifiedPresets([]);
  }, [openModal]);

  return (<>
    <li className='dropdown-item mr-1 ml-1' onClick={handleOpenModal}>{I18n.t('manage')}</li>
    {openModal &&
      <Portal
        id={MANAGE_PRESETS_MODAL_ID}
        size='lg'
        title={I18n.t('manage_presets')}
        saveLabel={I18n.t('save')}
        cancelLabel={I18n.t('close')}
        onClose={handleCloseModal}
        onSave={handleSave}
        saving={saving}
        component={ModalComponent}
      >
        {displayedPresets.length == 0 ? <span>{I18n.t('no_presets')}</span> : presetList()}
      </Portal>
    }
  </>);

  function handleSave() {
    if (modifiedPresets.length != 0 && !confirm(I18n.t('save_confirm'))) return;
    setSaving(true);

    let default_presets = {
      default_preset_id: displayedPresets.find((preset) => preset.default == true)?.id || null,
      project_default_preset_id: displayedPresets.find((preset) => preset.default_in_project_context == true)?.id || null
    }

    fetchJson(
      datagrid_modify_presets_in_batch_path({ datagrid_id: datagrid_id, context_name: context_name, _options: true }),
      { method: 'POST', body: JSON.stringify({modified_presets: modifiedPresets, default_presets: default_presets, preset_order: Object.assign({}, displayedPresets.map(v => v.id))}) }
    ).then(_res => {
      setPresets(displayedPresets);

      if (default_presets.default_preset_id) {
        restoreState(displayedPresets.find((preset)=> preset.id == default_presets.default_preset_id))
      } else {
        resetState()
      }
    });
    setSaving(false);

    closeModal(MANAGE_PRESETS_MODAL_ID);
    setOpenModal(false)
  }

  function presetList() {
    return (
      <DndProvider backend={Backend}>
        <table className='table table-striped table-hover'>
          <thead>
            <tr>
              <th style={{width: '15px'}}></th>
              <th>{I18n.t('id')}</th>
              <th>{I18n.t('name')}</th>
              {context_name != 'datagrid_collection' &&
                <>
                  <th>{I18n.t('default_in_fund')}</th>
                  <th>{I18n.t('default_in_project')}</th>
                  <th>{I18n.t('public')}</th>
                </>
              }
              {permissions.presets_lock &&
                <th>{I18n.t('locked')}</th>
              }
              <th>{I18n.t('visibility')}</th>
              <th style={{width: '15px'}}></th>
            </tr>
          </thead>
          <tbody className='table-hover dragHandle'>
            {displayedPresets.map((preset, rowIndex) => (
              <React.Fragment key={rowIndex}>
                <DropArea
                  accept={DND_ITEM_TYPE}
                  item={{ type: DND_ITEM_TYPE, index: rowIndex }}
                  onMove={moveRow}
                  render={({dropRef, dragRef, isDragging}) => {
                    return (
                      <tr ref={dropRef} style={{opacity: isDragging ? 0.5 : 1}}>
                        <td>
                          <DragHandle dragRef={dragRef}/>
                        </td>
                        <td>
                          <div style={{marginTop: 5}}>{preset.id}</div>
                        </td>
                        <td>
                          {preset.id == editRowId && !preset.is_locked ?
                            <input
                              value={preset.name}
                              onChange={(e) => updatePresetProperty(preset.id, 'name', e.target.value)}
                              onBlur={(_e) => setEditRowId(null)}
                              autoFocus
                              className='form-control'
                              style={{padding: '5px', height: '29px'}}
                            />
                            :
                            <a style={{color: 'black', display: 'block', marginTop: '5px'}} onClick={() => setEditRowId(preset.id)} >{`${preset.name}`}</a>
                          }
                        </td>
                        {context_name != 'datagrid_collection' &&
                          <>
                            <td>
                              <ToggleSwitch
                                isOn={preset.default}
                                handleToggle={() => handleChangeDefault(preset, 'default')}
                                name={`preset-${preset.id}`}
                              />
                            </td>
                            <td>
                              <ToggleSwitch
                                isOn={preset.default_in_project_context}
                                handleToggle={() => handleChangeDefault(preset, 'default_in_project_context')}
                                name={`project-preset-${preset.id}`}
                              />
                            </td>
                            <td>
                              <ToggleSwitch
                                  isOn={preset.is_public}
                                  handleToggle={() => updatePresetProperty(preset.id, 'is_public', !preset.is_public)}
                                  name={`public-${preset.id}`}
                              />
                            </td>
                          </>
                        }
                        {permissions.presets_lock &&
                          <td>
                            <ToggleSwitch
                              isOn={preset.is_locked}
                              handleToggle={() => updatePresetProperty(preset.id, 'is_locked', !preset.is_locked)}
                              name={`locked-${preset.id}`}
                            />
                          </td>
                        }
                        <td>
                          {!preset.is_locked ?
                            <select
                              name={`visibility-${preset.id}`}
                              value={preset.visibility}
                              onChange={(e) => updatePresetProperty(preset.id, 'visibility', e.target.value)}
                              className='form-control'
                              style={{padding: '5px', height: '29px'}}
                            >
                              {['all', 'project_context', 'fund_context', 'none'].map((visibility) => {
                                return <option key={`visibility-${preset.id}-${visibility}`} value={visibility}>{I18n.t(visibility)}</option>;
                              })}
                            </select>
                            :
                            <div style={{marginTop: '5px'}}>{I18n.t(preset.visibility)}</div>
                          }
                        </td>
                        <td>
                          {!preset.is_locked &&
                            <Button elemType='button'  size='xs' type='danger' style={{marginTop: '5px'}} onClick={() => handleDeletePreset(preset)}>
                              <Adalicon type='trash'/>
                            </Button>
                          }
                        </td>
                      </tr>
                    )
                  }}
                />
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </DndProvider>
    )
  }
}

export default ManagePresetsModalTrigger;
